import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { CheckCircle, XCircle, Clock, Eye, Search, RefreshCw } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ApplicationManagementTab({ token, endpoint, title, nameField, idField }) {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(null);

  const fetchApps = async () => {
    try {
      const res = await axios.get(`${API}${endpoint}`, { headers: { Authorization: `Bearer ${token}` } });
      setApps(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchApps(); }, []);// eslint-disable-line react-hooks/exhaustive-deps

  const updateStatus = async (id, status, reason = null) => {
    try {
      const data = { status };
      if (reason) data.rejection_reason = reason;
      await axios.put(`${API}${endpoint}/${id}`, data, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Application ${status.replace('_', ' ')}`);
      fetchApps();
      setShowRejectModal(null);
      setRejectionReason('');
    } catch (err) { toast.error('Failed to update'); }
  };

  const statusColor = (s) => {
    const m = { 
      submitted: 'bg-blue-100 text-blue-700', 
      in_review: 'bg-amber-100 text-amber-700',
      incomplete: 'bg-orange-100 text-orange-700',
      resubmission: 'bg-purple-100 text-purple-700',
      approved: 'bg-green-100 text-green-700', 
      rejected: 'bg-red-100 text-red-700'
    };
    return m[s] || 'bg-slate-100 text-slate-700';
  };

  if (loading) return <p className="text-sm text-slate-500 py-8 text-center">Loading applications...</p>;

  return (
    <div data-testid={`admin-${title.toLowerCase().replace(' ', '-')}-tab`}>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-slate-600"><strong>{apps.length}</strong> {title} applications</p>
      </div>

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4">
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold text-[#1B2A4A] mb-4">Reject Application</h3>
              <div className="mb-4">
                <Label className="text-sm">Rejection Reason</Label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  className="w-full mt-1 p-2 border border-slate-200 rounded-sm text-sm"
                  rows={3}
                  placeholder="Please provide a reason for rejection..."
                />
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => { setShowRejectModal(null); setRejectionReason(''); }} className="rounded-sm">Cancel</Button>
                <Button onClick={() => updateStatus(showRejectModal, 'rejected', rejectionReason)} className="bg-red-600 text-white rounded-sm">Reject</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {apps.length === 0 ? (
        <p className="text-sm text-slate-500 py-8 text-center">No {title.toLowerCase()} applications yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#F8FAFC]">
                <TableHead className="text-xs font-bold">{title === 'Examination Center' ? 'Center Name' : 'Institution/Applicant'}</TableHead>
                {idField && <TableHead className="text-xs font-bold">ID</TableHead>}
                <TableHead className="text-xs font-bold">Country</TableHead>
                <TableHead className="text-xs font-bold">Date</TableHead>
                <TableHead className="text-xs font-bold">Status</TableHead>
                <TableHead className="text-xs font-bold">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {apps.map((app) => (
                <TableRow key={app.id}>
                  <TableCell>
                    <p className="text-sm font-medium text-[#1B2A4A]">{app[nameField]}</p>
                    <p className="text-xs text-slate-500">{app.email || app.contact_person}</p>
                  </TableCell>
                  {idField && <TableCell className="text-xs font-mono text-slate-500">{app[idField] || '-'}</TableCell>}
                  <TableCell className="text-sm text-slate-600">{app.country}</TableCell>
                  <TableCell className="text-xs text-slate-500">{new Date(app.created_at).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <Badge className={`text-xs rounded-sm ${statusColor(app.status)}`}>{app.status?.replace('_', ' ')}</Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      <Button size="sm" className="h-6 px-2 bg-green-600 hover:bg-green-700 text-white text-xs rounded-sm" onClick={() => updateStatus(app.id, 'approved')}>
                        <CheckCircle className="h-3 w-3 mr-1" />Approve
                      </Button>
                      <Button size="sm" variant="outline" className="h-6 px-2 text-xs rounded-sm border-amber-400 text-amber-600" onClick={() => updateStatus(app.id, 'in_review')}>
                        <Clock className="h-3 w-3 mr-1" />Review
                      </Button>
                      <Button size="sm" variant="outline" className="h-6 px-2 text-xs rounded-sm border-orange-300 text-orange-600" onClick={() => updateStatus(app.id, 'incomplete')}>
                        Incomplete
                      </Button>
                      <Button size="sm" variant="outline" className="h-6 px-2 text-xs rounded-sm border-purple-300 text-purple-600" onClick={() => updateStatus(app.id, 'resubmission')}>
                        <RefreshCw className="h-3 w-3 mr-1" />Resubmit
                      </Button>
                      <Button size="sm" variant="outline" className="h-6 px-2 text-xs rounded-sm border-red-300 text-red-600" onClick={() => setShowRejectModal(app.id)}>
                        <XCircle className="h-3 w-3 mr-1" />Reject
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

export default ApplicationManagementTab;
